import { useCallback, useEffect, useRef } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { get, set } from 'react-hook-form'
import type {
  DefaultValues,
  FieldValues,
  Path,
  SubmitErrorHandler,
  SubmitHandler,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormReset,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  useIsSavedIssuerFixed,
  useIssuerOptions,
  useIssuerOptionsPending,
  useLoadedIssuerOptions,
} from '~/components/bidding/common/IssuerSelector'
import {
  type IssuerAreaSelectOption,
  type IssuerOption,
  findIssuerOption,
  selectedAreaOf,
} from '~/components/bidding/common/bidIssuer'
import { useClearAreasWhenIssuerOptionsArrive } from '~/pages/SpotBidding/hooks/useClearAreasWhenIssuerOptionsArrive'
import { useSelectableAreas } from '~/pages/SpotBidding/hooks/useSelectableAreas'
import {
  type DraftIssuerReselectBlock,
  draftIssuerReselectBlock,
} from '~/pages/SpotBidding/models/draftIssuerReselect'

type Params<T extends FieldValues> = {
  deliveryDate: Date
  active: boolean
  mode: 'create' | 'duplicate' | 'rebid' | 'edit'
  readOnly: boolean
  savedIssuer?: LicenseType
  issuerField: Path<T>
  issuerValue: LicenseType
  areaFields: ReadonlyArray<Path<T>>
  areas: ReadonlyArray<AreaCode | undefined>
  candidateAreaCodes: readonly AreaCode[]
  disabledAreas: ReadonlySet<AreaCode> | undefined
  keepAreaCodes: readonly AreaCode[]
  unselectedValue?: AreaCode
  getValues: UseFormGetValues<T>
  setValue: UseFormSetValue<T>
  setError: UseFormSetError<T>
  handleSubmit: UseFormHandleSubmit<T>
  reset?: UseFormReset<T>
}

type Result<T extends FieldValues> = {
  options: IssuerOption[]
  selected: IssuerOption | undefined
  fixed: boolean
  areaSelectOptionsFor: (
    issuer: IssuerOption | undefined,
    selectedArea: AreaCode | undefined,
  ) => IssuerAreaSelectOption[]
  onChange: (next: LicenseType, option: IssuerOption | undefined) => void
  reselectBlock: DraftIssuerReselectBlock | undefined
  resetForm: (values?: T | DefaultValues<T>, mode?: 'refresh') => void
  submit: (valid: SubmitHandler<T>, invalid: SubmitErrorHandler<T>) => void
}

export function useSpotBidIssuerForm<T extends FieldValues>(
  p: Params<T>,
): Result<T> {
  const {
    setValue,
    issuerField,
    issuerValue,
    active,
    readOnly,
    getValues,
    reset,
  } = p
  const edit = p.mode === 'edit'
  const { t } = useTranslation()
  const options = useIssuerOptions(p.deliveryDate)
  const loaded = useLoadedIssuerOptions(p.deliveryDate)
  const pending = useIssuerOptionsPending(p.deliveryDate)
  const fixed = useIsSavedIssuerFixed(p.deliveryDate, p.savedIssuer, p.active)
  const selected = findIssuerOption(options, p.issuerValue)
  const {
    areaSelectOptionsFor,
    clearAreasOutsideCandidates,
    clearAreasUnselectableForIssuer,
    isAreaTradable,
  } = useSelectableAreas({
    ...p,
    isDisabled: p.readOnly || !p.active,
    areaFieldNames: p.areaFields,
  })
  const onChange = useCallback(
    (next: LicenseType, option: IssuerOption | undefined) => {
      setValue(issuerField, next as never, { shouldValidate: true })
      if (!edit) clearAreasUnselectableForIssuer(option)
    },
    [setValue, issuerField, edit, clearAreasUnselectableForIssuer],
  )
  const reconcile = useCallback(() => {
    if (!active || readOnly) return
    let current = getValues(issuerField) as LicenseType
    if (
      !pending &&
      (!edit || loaded !== undefined) &&
      current !== LicenseType.UNSPECIFIED &&
      findIssuerOption(options, current) === undefined
    ) {
      onChange(LicenseType.UNSPECIFIED, undefined)
      current = LicenseType.UNSPECIFIED
    }
    if (
      !edit &&
      current === LicenseType.UNSPECIFIED &&
      options[0] !== undefined
    )
      onChange(options[0].issuerType, options[0])
  }, [
    active,
    readOnly,
    edit,
    getValues,
    issuerField,
    pending,
    loaded,
    options,
    onChange,
  ])
  useEffect(() => {
    reconcile()
  }, [reconcile, issuerValue])
  useClearAreasWhenIssuerOptionsArrive({
    deliveryDate: p.deliveryDate,
    isActive: p.active && p.mode === 'create' && !p.readOnly,
    readIssuerType: () => p.getValues(p.issuerField) as LicenseType,
    clearAreasUnselectableForIssuer,
  })
  const areas = p.areas
    .map(selectedAreaOf)
    .filter((area): area is AreaCode => area !== undefined)
  const reselectBlock =
    edit && !p.readOnly && !fixed && areas.length > 0
      ? draftIssuerReselectBlock(options, selected, areas)
      : undefined
  const resetContext = useRef({
    reconcile,
    getValues,
    issuerField,
    areaFields: p.areaFields,
  })
  resetContext.current = {
    reconcile,
    getValues,
    issuerField,
    areaFields: p.areaFields,
  }
  const resetForm = useCallback(
    (values?: T | DefaultValues<T>, mode?: 'refresh'): void => {
      const context = resetContext.current
      if (mode === 'refresh' && values !== undefined) {
        const restored = structuredClone(values)
        for (const field of [context.issuerField, ...context.areaFields]) {
          set(restored, field, context.getValues(field))
        }
        reset?.(restored)
      } else {
        reset?.(values)
      }
      context.reconcile()
    },
    [reset],
  )
  const submitContext = useRef({
    options,
    fixed,
    edit,
    areaFields: p.areaFields,
    isAreaTradable,
  })
  submitContext.current = {
    options,
    fixed,
    edit,
    areaFields: p.areaFields,
    isAreaTradable,
  }
  const submit = (
    valid: SubmitHandler<T>,
    invalid: SubmitErrorHandler<T>,
  ): void => {
    if (p.readOnly) return
    clearAreasOutsideCandidates()
    void p.handleSubmit((data, event) => {
      const latest = submitContext.current
      const currentIssuer = get(data, p.issuerField) as LicenseType
      const currentAreas = latest.areaFields
        .map((field) =>
          selectedAreaOf(get(data, field) as AreaCode | undefined),
        )
        .filter((area): area is AreaCode => area !== undefined)
      if (
        latest.edit &&
        !latest.fixed &&
        currentAreas.length > 0 &&
        draftIssuerReselectBlock(
          latest.options,
          findIssuerOption(latest.options, currentIssuer),
          currentAreas,
        ) !== undefined
      )
        return
      for (const field of latest.areaFields) {
        const area = selectedAreaOf(get(data, field) as AreaCode | undefined)
        if (area !== undefined && !latest.isAreaTradable(area)) {
          p.setError(field, {
            message: t('page:biddingSpot.form.areaNotTradableError'),
          })
          return
        }
      }
      return valid(data, event)
    }, invalid)()
  }
  return {
    options,
    selected,
    fixed,
    areaSelectOptionsFor,
    onChange,
    reselectBlock,
    resetForm,
    submit,
  }
}
